import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Btn, DeskSupport, Kv, PageBody, PageHead, Panel, RailCard, Sec, StatStrip } from "@/components/portal/crm/kit";
import { ApiKeyPanel } from "@/components/portal/api/ApiKeyPanel";
import { WebhookPanel } from "@/components/portal/api/WebhookPanel";
import { ApiActivity, ApiCases } from "@/components/portal/api/ApiCases";
import { ApiGuide } from "@/components/portal/api/ApiGuide";
import { AdminPreviewBanner } from "./Portal";

/** The API tab — only for introducers the Connexis desk has switched on. */
export default function ApiAccess() {
  const { me, leads, reload, isAdmin } = useOutletContext();
  const [info, setInfo] = useState(null);
  const [activity, setActivity] = useState({ requests: [], deliveries: [] });
  const on = !!me?.api_enabled && !me?.seat;

  const load = useCallback(() => {
    if (!on) return;
    api.get("/refer/api").then((r) => setInfo(r.data)).catch(() => setInfo(null));
    api.get("/refer/api/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, [on]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => { if (!document.hidden) { load(); reload(); } }, 30000);
    return () => clearInterval(t);
  }, [on, load, reload]);

  if (isAdmin) return <AdminPreviewBanner what="introducer API tab" />;
  if (!on) {
    return (
      <>
        <PageHead eyebrow="Integrations" title="API" testId="api-head"
                  lede="Send cases straight from your own system, and get every stage move back automatically." />
        <Panel testId="api-not-enabled">
          <p style={{ margin: "0 0 14px", fontSize: 14 }}>
            API access isn't switched on for your account yet. Message the Connexis desk and we'll set you up — it usually takes a few minutes.
          </p>
          <Btn kind="primary" sm as={Link} to="/introducer/messages" testId="api-not-enabled-message">Message the desk</Btn>
        </Panel>
      </>
    );
  }
  const s = info?.stats || {};
  const hook = info?.webhook || {};

  return (
    <div data-testid="introducer-api-page">
      <PageHead eyebrow="Integrations" title="API" testId="api-head"
                lede="Send cases straight from your own system. Each one lands in My cases, a person on the desk places it by hand, and every stage move comes back to you." />
      <PageBody rail={
        <>
          <RailCard title="How it works" testId="api-rail-how">
            <Kv left rows={[
              ["1", "Your system sends a case"],
              ["2", "It lands at New in My cases"],
              ["3", "The desk places it by hand"],
              ["4", "Every move comes back to you"],
            ]} />
          </RailCard>
          <DeskSupport body="Wiring up your CRM or a form? Message us and we'll check your first case with you." />
        </>
      }>
        <StatStrip testId="api-stats" items={[
          { k: "Sent by API", v: String(s.cases ?? 0), h: `${s.last_24h ?? 0} in the last 24 hours`, testId: "api-stat-cases" },
          { k: "Flagged repeats", v: String(s.duplicates ?? 0), h: "Accepted, checked by the desk", testId: "api-stat-dups" },
          { k: "Funded", v: String(s.funded ?? 0), h: "From API cases", tone: s.funded ? "green" : undefined, testId: "api-stat-funded" },
          { k: "Stage updates", v: hook.enabled ? "On" : "Off", h: hook.enabled ? (hook.url || "").replace(/^https:\/\//, "").slice(0, 34) : "Add your address below", testId: "api-stat-webhook" },
        ]} />
        <Sec title="Connect" sub="Two things to set up, once.">
          <div style={{ display: "grid", gap: 18 }}>
            <ApiKeyPanel info={info} onChange={load} />
            <WebhookPanel info={info} deliveries={activity.deliveries} onChange={load} />
          </div>
        </Sec>
        <Sec title="Cases sent in by API" sub="Newest first. Open any one for the full case record.">
          <ApiCases leads={leads} />
        </Sec>
        <Sec title="For your developer">
          <div style={{ display: "grid", gap: 18 }}>
            <ApiGuide info={info} />
            <ApiActivity rows={activity.requests} />
          </div>
        </Sec>
      </PageBody>
    </div>
  );
}
